import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Upload } from 'lucide-react';
import AlertDialog from './AlertDialog';
import { useAlertDialog } from '../hooks/useAlertDialog';

interface GiftFormProps {
  onClose: () => void;
  onSubmit: (data: GiftFormData) => Promise<void>;
  initialData?: {
    id: string;
    name: string;
    description: string;
    price: number;
    pixKey: string;
    imageUrl: string;
  };
  mode?: 'create' | 'edit';
}

export interface GiftFormData {
  id?: string;
  name: string;
  description: string;
  price: number;
  pixKey: string;
  imageUrl?: string;
  imageFile?: File;
}

const formatPrice = (value: string) => {
  let numbers = value.replace(/\D/g, '');
  const price = Number(numbers) / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const GiftForm: React.FC<GiftFormProps> = ({ 
  onClose, 
  onSubmit, 
  initialData,
  mode = 'create' 
}) => {
  const [preview, setPreview] = useState<string | null>(initialData?.imageUrl || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceInput, setPriceInput] = useState(
    initialData ? formatPrice(String(initialData.price * 100)) : ''
  );
  const { isOpen, title, message, setAlert, clearAlert } = useAlertDialog();
  
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<GiftFormData>({
    defaultValues: initialData ? {
      id: initialData.id,
      name: initialData.name,
      description: initialData.description,
      price: initialData.price,
      pixKey: initialData.pixKey,
      imageUrl: initialData.imageUrl
    } : undefined
  });
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPreview(URL.createObjectURL(file));
      setValue('imageFile', file);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/\D/g, '');
    
    if (numbers) {
      const formattedValue = formatPrice(numbers);
      setPriceInput(formattedValue);
      const numericValue = Number(numbers) / 100;
      setValue('price', numericValue);
    } else {
      setPriceInput('');
      setValue('price', 0);
    }
  };

  const onSubmitHandler = async (data: GiftFormData) => {
    try {
      setIsSubmitting(true);
      
      if (typeof data.price !== 'number' || isNaN(data.price)) {
        setAlert('Erro', 'Preço inválido');
        return;
      }

      // If in edit mode and no new image is selected, keep the existing imageUrl
      if (mode === 'edit' && !data.imageFile) {
        data.imageUrl = initialData?.imageUrl;
      }
      
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error submitting gift:', error);
      setAlert('Erro', 'Erro ao adicionar presente. Por favor, verifique os dados e tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-playfair font-medium text-wedding-blue-800">
              {mode === 'create' ? 'Adicionar Novo Presente' : 'Editar Presente'}
            </h3>
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmitHandler)} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Presente
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...register('name', { required: 'Nome é obrigatório' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    {...register('description', { required: 'Descrição é obrigatória' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                    Preço
                  </label>
                  <input
                    id="price"
                    type="text"
                    value={priceInput}
                    onChange={handlePriceChange}
                    placeholder="R$ 0,00"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${
                      errors.price ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.price && (
                    <p className="text-red-500 text-xs mt-1">{errors.price.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="pixKey" className="block text-sm font-medium text-gray-700 mb-1">
                    Chave Pix
                  </label>
                  <input
                    id="pixKey"
                    type="text"
                    {...register('pixKey', { required: 'Chave Pix é obrigatória' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${
                      errors.pixKey ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.pixKey && (
                    <p className="text-red-500 text-xs mt-1">{errors.pixKey.message}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagem do Presente
                </label>
                
                <div className="border-2 border-dashed rounded-lg p-4">
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-48 object-contain rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreview(null);
                          setValue('imageFile', undefined);
                          setValue('imageUrl', undefined);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
                      <Upload size={32} className="text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Clique para selecionar uma imagem</span>
                      <input
                        type="file"
                        accept="image/*"
                        {...register('imageFile', { 
                          required: mode === 'create' ? 'Imagem é obrigatória' : false 
                        })}
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                  {errors.imageFile && (
                    <p className="text-red-500 text-xs mt-1">{errors.imageFile.message}</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Salvando...' : mode === 'create' ? 'Adicionar Presente' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <AlertDialog
        isOpen={isOpen}
        title={title}
        message={message}
        onClose={clearAlert}
      />
    </>
  );
};

export default GiftForm;